import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { assigneeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { assigneeSortFn } from "../../sorting/utils"
import { AssigneeSelectGroup, type AssigneeSelectGroupProps } from "../MenuSelectGroups/AssigneeSelectGroup"

type AssigneeSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	AssigneeSelectGroupProps &
	MenuVariantProps & {
		children: ReactElement
	}

export const AssigneeSelect = memo<AssigneeSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// AssigneeSelectGroup group props
		users,
		required,
		defaultValue,
		value,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedUsers = useSort(users, assigneeSortFn)

	const [filteredUsers, filterValue, setFilterValue] = useTextFilter<User | null>(sortedUsers, assigneeFilterCallback)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredUsers.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<AssigneeSelectGroup
							users={filteredUsers}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					) : (
						<AssigneeSelectGroup
							users={filteredUsers}
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})

AssigneeSelect.displayName = "AssigneeSelect"
